// src/analytics/analyticsService.ts
//
// Web analytics service. Deliberately mirrors the shape of your React Native
// analyticsService (logEvent / swallow / never-crash) so the mental model is
// identical across app + web.

import { logEvent } from "firebase/analytics";
import { initAnalytics, getAnalyticsInstance } from "./firebase";
import { getUtmParams } from "./utm";

// Web-specific event catalog. Keep names snake_case to match GA4 + your RN app.
export const WebAnalyticsEvent = {
  // page_view / session_start / first_visit are collected automatically by GA4.
  STORE_CLICK: "store_click", // clicked App Store / Google Play
  CTA_CLICK: "cta_click", // generic hero/footer download CTA
  SCROLL_DEPTH: "scroll_depth", // 25 / 50 / 75 / 100 %
  FAQ_OPEN: "faq_open",
  LANGUAGE_VIEW: "language_view", // which locale the visitor landed on
} as const;

export type WebAnalyticsEventName =
  (typeof WebAnalyticsEvent)[keyof typeof WebAnalyticsEvent];

type ParamValue = string | number | boolean;
type Params = Record<string, ParamValue>;

const swallow = (op: string, error: unknown) => {
  // No Crashlytics on web; console.debug is enough and stays silent in prod UIs.
  if (process.env.NODE_ENV !== "production") {
    // eslint-disable-next-line no-console
    console.debug(
      `[analytics] ${op} failed:`,
      error instanceof Error ? error.message : String(error)
    );
  }
};

class WebAnalyticsService {
  private ready = false;

  // Call once near app startup. Safe to call before initAnalytics resolves —
  // events fired before readiness are simply dropped (acceptable for a landing
  // page; the important conversions happen after the user has seen content).
  init = async (): Promise<void> => {
    try {
      const instance = await initAnalytics();
      this.ready = instance !== null;
    } catch (error) {
      swallow("init", error);
    }
  };

  logEvent = (name: WebAnalyticsEventName, params?: Params): void => {
    try {
      const instance = getAnalyticsInstance();
      if (!instance) return; // not on prod host, or unsupported env
      // UTM first so explicit params win on key collision. getUtmParams never
      // throws and returns {} for organic traffic (no utm_* keys added).
      logEvent(instance, name, { ...getUtmParams(), ...params });
    } catch (error) {
      swallow(`logEvent(${name})`, error);
    }
  };
}

export const analytics = new WebAnalyticsService();
